import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { type ApplicationConfig, ErrorHandler, Injectable, importProvidersFrom, inject, isDevMode, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { SharedAzureAuthModule } from '@dtis/angular';
import { provideTransloco, type Translation, type TranslocoLoader } from '@jsverse/transloco';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { environment } from '../environments/environment';
import { ErrorHandlerService } from '../services/error-handler.service';
import { NoCacheHeadersInterceptor } from '../services/no-cache-interceptor';
import { routes } from './app.routing';
import { MultiViewState } from './states/multi-view/multi-view.state';
import { SettingsState } from './states/settings/settings.state';
import { STATE_MIGRATIONS } from './states/state-migrations';
import { checkAndMigrateSchemaVersion, sanitizeStateAfterDeserialize } from './states/state-sanitizer';

// Check schema version on module load — runs before Angular bootstraps
checkAndMigrateSchemaVersion();

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheHeadersInterceptor,
      multi: true,
    },

    { provide: ErrorHandler, useClass: ErrorHandlerService },
    provideAngularSvgIcon(),
    provideRouter(routes),
    provideStore([SettingsState, MultiViewState], {
      developmentMode: !environment.production,
    }),
    withNgxsLoggerPlugin({ disabled: true }),
    withNgxsReduxDevtoolsPlugin({ disabled: environment.production }),
    withNgxsStoragePlugin({
      keys: '*',
      migrations: STATE_MIGRATIONS,
      // Sanitize every state slice after deserialization from localStorage.
      // This catches missing fields, type mismatches, and corrupted data that
      // the version-based migrations above cannot handle (e.g. new fields added
      // without a migration, or manually corrupted storage).
      afterDeserialize: sanitizeStateAfterDeserialize,
    }),
    withNgxsRouterPlugin(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'de'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    importProvidersFrom(SharedAzureAuthModule.forRoot(environment)),
    provideHttpClient(withInterceptorsFromDi()),
  ],
};
